import { ReactNode, useEffect, useMemo } from 'react';
import { applyBrandTheme, getRememberedBrand, safeBrandLogoUrl } from '../lib/brand';

interface Props {
  /** Screen heading (e.g. "Participant Portal", "Reset your password"). */
  title: ReactNode;
  /** Optional supporting line under the heading. */
  subtitle?: ReactNode;
}

/**
 * The brand block atop every pre-auth screen (sign-in, forgot-password,
 * onboarding). It wears the participant's last-known program brand — the same
 * logo the logged-in header shows — resolved from the device cache written after
 * their previous sign-in (see lib/brand). A device that has never signed in, or
 * a cleared cache, falls back to the neutral "AA" badge and the default palette.
 */
export default function AuthBrand({ title, subtitle }: Props) {
  const brand = useMemo(() => getRememberedBrand(), []);
  const logoUrl = useMemo(() => safeBrandLogoUrl(brand?.logoUrl), [brand]);

  // Re-skin this screen's buttons/inputs/links with the remembered palette. On a
  // fresh device `brand` is undefined and the neutral design tokens stay in place.
  useEffect(() => {
    applyBrandTheme(brand);
  }, [brand]);

  return (
    <div className="auth-brand">
      {logoUrl ? (
        <img className="logo" src={logoUrl} alt={brand?.name ?? ''} />
      ) : (
        <div className="logo" aria-hidden>
          AA
        </div>
      )}
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}
